import { tt } from '@agos/shared'

/**
 * *Externé Firmy → Firmy* - the companies this restaurant invoices, FRONTEND.md §9.347.
 *
 * **Read off the wire and kept whole**, which is FB-505's lesson: the endpoint answers eleven fields
 * and a parser that drops one is a field nobody can edit. `enabled` is read as `!== false` rather
 * than `=== true`, so a row from an older shape that omits it is on rather than invisible.
 */

export interface InvoiceCompany {
  id: number
  name: string
  address: string | null
  ico: string | null
  dic: string | null
  icDph: string | null
  email: string | null
  notes: string | null
  enabled: boolean
}

function text(row: Record<string, unknown>, key: string): string | null {
  const value = row[key]
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

export function companiesOf(payload: unknown): InvoiceCompany[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        name: text(row, 'name') ?? '',
        address: text(row, 'address'),
        ico: text(row, 'ico'),
        dic: text(row, 'dic'),
        icDph: text(row, 'icDph'),
        email: text(row, 'email'),
        notes: text(row, 'notes'),
        // A row with no `enabled` at all is on: the safe direction is *visible and switchable off*,
        // not *missing from the list nobody can find it in*.
        enabled: row.enabled !== false,
      },
    ]
  })
}

/** The form's own shape - strings, because these are inputs. */
export interface CompanyForm {
  name: string
  address: string
  ico: string
  dic: string
  icDph: string
  email: string
  notes: string
  enabled: boolean
}

export function emptyCompanyForm(): CompanyForm {
  return { name: '', address: '', ico: '', dic: '', icDph: '', email: '', notes: '', enabled: true }
}

export function companyFormOf(company: InvoiceCompany): CompanyForm {
  return {
    name: company.name,
    address: company.address ?? '',
    ico: company.ico ?? '',
    dic: company.dic ?? '',
    icDph: company.icDph ?? '',
    email: company.email ?? '',
    notes: company.notes ?? '',
    enabled: company.enabled,
  }
}

const NAME_LIMIT = 200
const IDENTIFIER_LIMIT = 32
/** Digits, letters, spaces and separators - every European identifier shape. Mirrors the server. */
const IDENTIFIER = /^[A-Za-z0-9 /.-]+$/

/**
 * Why this company cannot be saved, or `null`.
 *
 * **Mirrors `InvoiceCompanyService` rather than inventing a second rulebook**, including the part
 * that is deliberately loose: an identifier is checked for *shape*, never against a register. The
 * owner's own suppliers carry values this repository cannot verify, and refusing an unverifiable
 * IČO would stop an invoice being issued at all.
 */
export function companyProblem(form: CompanyForm): string | null {
  const name = form.name.trim()
  if (name === '') return tt('Názov firmy je povinný.')
  if (name.length > NAME_LIMIT) return tt('Názov firmy je príliš dlhý.')
  for (const [field, label] of [
    ['ico', tt('IČO')],
    ['dic', tt('DIČ')],
    ['icDph', tt('IČ DPH')],
  ] as const) {
    const value = form[field].trim()
    if (value === '') continue
    if (value.length > IDENTIFIER_LIMIT || !IDENTIFIER.test(value)) {
      return `${label}: ${tt('toto nie je platný identifikátor.')}`
    }
  }
  if (form.email.trim() !== '' && !form.email.includes('@')) {
    return tt('E-mail musí obsahovať @.')
  }
  return null
}

/**
 * The body sent on create, and on patch.
 *
 * **Every field, every time, including the blanks** - a blank clears the field, which is what
 * somebody deleting a wrong IČO means. The server reads a blank string as `null`, so the two agree
 * without the screen having to send `null` itself.
 */
export function companyPayload(form: CompanyForm): Record<string, unknown> {
  return {
    name: form.name.trim(),
    address: form.address.trim(),
    ico: form.ico.trim(),
    dic: form.dic.trim(),
    icDph: form.icDph.trim(),
    email: form.email.trim(),
    notes: form.notes.trim(),
    enabled: form.enabled,
  }
}

/** What the row says under the name, so a company is recognisable without opening it. */
export function companyLine(company: InvoiceCompany): string {
  const parts = [company.ico === null ? null : `${tt('IČO')} ${company.ico}`, company.address].filter(
    (part): part is string => part !== null && part !== '',
  )
  return parts.length === 0 ? tt('Bez ďalších údajov') : parts.join(' · ')
}

/** Searching a list of companies: the name, the identifiers and the address all count. */
export function matchingCompanies(
  companies: readonly InvoiceCompany[],
  search: string,
): InvoiceCompany[] {
  const needle = search.trim().toLowerCase()
  if (needle === '') return [...companies]
  return companies.filter((company) =>
    [company.name, company.ico, company.dic, company.icDph, company.address]
      .filter((field): field is string => typeof field === 'string')
      .some((field) => field.toLowerCase().includes(needle)),
  )
}
